import React, { useEffect, useState } from "react";
import Layout from "./Layout";
import { api, baseURL, customerReqConfig } from "../../include/api";
import { getCart, getCustomer, isEmptyObj, setToLocalStorage } from "../../include/function";
import { Link, useNavigate } from "react-router-dom";
import { useNotification } from "../../Toast/ToastProvider";
import { useCart } from "../context/UserCartCountContext";
import { CHECKOUT, ERROR, SUCCESS } from "../../include/constant"
import { addToCartHandler, loadCartData } from "../../helper/cartUtils";

const ComparePage = () => {
  const [compareList, setCompareList] = useState([]);
  const [brands, setBrands] = useState([]);
  const [brandProducts, setBrandProducts] = useState({});
  const [loadingAction, setLoadingAction] = useState({ type: null, id: null });
  const customer = getCustomer();
  const navigate = useNavigate();
  const toast = useNotification();
  const [addedToCartMap, setAddedToCartMap] = useState({});
  const { fetchCartCount, setTotalWishlistItems } = useCart();

  useEffect(() => {
    if (customer?.id) {
      loadCartData(customer.id, () => { }, setAddedToCartMap);
    }
  }, [customer?.id]);

  const [slots, setSlots] = useState([
    { brandId: "", productId: "", product: null },
  ]);

  useEffect(() => {
    const handleCartUpdate = () => {
      if (customer?.id) {
        loadCartData(customer.id, () => { }, setAddedToCartMap);
      }
    };

    window.addEventListener("cartUpdated", handleCartUpdate);
    return () => window.removeEventListener("cartUpdated", handleCartUpdate);
  }, [customer?.id, setAddedToCartMap]);

  useEffect(() => {
    const saved = localStorage.getItem("compareList");
    if (saved) {
      setCompareList(JSON.parse(saved));
    }

    api
      .get(`/category/?parentId=-1`, customerReqConfig())
      .then((res) => {
        if (res.status === 200) setBrands(res.data.data);
      })
      .catch((err) => console.error("Failed to fetch brands", err));
  }, []);

  // fetch products by brand
  const fetchProductsByBrand = (brandId, slotIndex) => {
    if (!brandId) return;
    api
      .get(`/product/?brandId=${brandId}`, customerReqConfig())
      .then((res) => {
        if (res.status === 200) {
          setBrandProducts((prev) => ({
            ...prev,
            [brandId]: res.data.data,
          }));
        }
      })
      .catch((err) => console.error("Failed to fetch products", err));
  };

  const handleBrandChange = (slotIndex, brandId) => {
    const newSlots = [...slots];
    newSlots[slotIndex].brandId = brandId;
    newSlots[slotIndex].productId = "";
    newSlots[slotIndex].product = null;
    setSlots(newSlots);
    fetchProductsByBrand(brandId, slotIndex);
  };

  const handleProductChange = (slotIndex, productId) => {
    const slot = slots[slotIndex];
    const products = brandProducts[slot.brandId] || [];
    const selectedProduct = products.find((p) => p.id === parseInt(productId));

    if (selectedProduct) {
      const newSlots = [...slots];
      newSlots[slotIndex].productId = productId;
      newSlots[slotIndex].product = selectedProduct;
      setSlots(newSlots);

      if (!compareList.some((p) => p.id === selectedProduct.id)) {
        const newCompareList = [...compareList, selectedProduct];
        setCompareList(newCompareList);
        localStorage.setItem("compareList", JSON.stringify(newCompareList));
      }
    }
  };

  const getSlotCount = () => {
    if (compareList.length === 1) return 3;
    if (compareList.length === 2) return 2;
    if (compareList.length === 3) return 1;
  };

  const slotCount = getSlotCount();

  if (compareList.length === 0 && slots.every((s) => !s.product)) {
    return (
      <p className="text-center py-4">No products selected for comparison.</p>
    );
  }

  const handleBuyNow = async (prod) => {
    if (!prod?.id || !prod.name || !prod.price) {
      toast({ type: ERROR, message: "Invalid product data." });
      return;
    }

    let totalDiscountValue = 0;
    if (prod.discountMode === "percent") {
      totalDiscountValue +=
        (parseFloat(prod.discountValue) / 100) * parseFloat(prod.price);
    } else if (prod.discountMode === "amount") {
      totalDiscountValue += parseFloat(prod.discountValue);
    }

    const totalPrice =
      parseFloat(prod.price || 0) -
      totalDiscountValue +
      parseFloat(prod.deliveryCharge || 0);

    const checkoutData = {
      totalItems: 1,
      billAmount: prod.price,
      totalDiscount: totalDiscountValue,
      totalDeliveryCharge: prod.deliveryCharge || 0,
      totalAmount: totalPrice,
      isBuyNow: true,
      orderItems: [
        {
          id: prod.id,
          productId: prod.id,
          qty: 1,
          price: prod.price,
          name: prod.name || "Unknown Product",
          mainImage: prod.mainImage || "",
          discountMode: prod.discountMode,
          discountValue: prod.discountValue,
          deliveryCharge: prod.deliveryCharge,
          total: totalPrice,
        },
      ],
    };

    await setToLocalStorage(CHECKOUT, checkoutData);
    navigate("/Checkout");
  };
  // Add to cart handler
  const handleAddToCart = async (prod) => {
    await addToCartHandler({
      product: prod,
      customer,
      setAddedToCartMap,
      fetchCartCount,
      toast,
      navigate,
      setLoading: (id) => setLoadingAction({ id, type: "add" }),
    });
  };
  // Helper to render 4 product cells (fill with data or empty)
  const renderProductCells = (field) => {
    const cells = [];
    for (let i = 0; i < 4; i++) {
      const product = compareList[i];
      cells.push(
        <td key={`cell-${field}-${i}`} className="compare-product-col">
          <h6 className="text-md fw-bold mb-0">
            {product ? product[field] || "N/A" : ""}
          </h6>
        </td>
      );
    }
    return cells;
  };

  // Helper to render 4 product card cells (fill with cards or empty placeholders)
  const renderProductCards = () => {
    const cells = [];
    for (let i = 0; i < 4; i++) {
      const product = compareList[i];
      cells.push(
        <td key={`card-${i}`} className="compare-product-col">
          {product ? (
            <div className="compare-card gap-5" style={{ height: "450px" }}>
              <img
                src={baseURL + "/" + product.mainImage}
                alt={product.name}
                className="compare-card-img px-52 py-32 mb-3"
              />
              <div className="d-flex flex-wrap justify-content-start ps-16 mt-16">
                <h6
                  className="text-md fw-bold"
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "200px",
                  }}
                >
                  {product.name.toUpperCase()}
                </h6>
                <div className="flex-align gap-8 flex-wrap mb-4">
                  <h6 className="text-xl mb-0 font-semibold text-black">
                    ₹{Math.round(
                      product.discountMode === "percent"
                        ? parseFloat(product.price) - (parseFloat(product.discountValue) / 100) * parseFloat(product.price)
                        : parseFloat(product.price) - parseFloat(product.discountValue)
                    ).toFixed(2)}
                  </h6>
                  {product.discountMode && product.discountValue > 0 && (
                    <span className="text-sm text-main-two-600">
                      Discount of {product.discountValue}
                      {product.discountMode === "percent" ? "%" : ""}
                    </span>
                  )}
                </div>
                <div className="flex-align gap-2 mt-2">
                  <p className="text-sm text-dark">M.R.P:</p>
                  <h5 className="text-xl text-gray-400 mb-0 line-through">
                    ₹{product.price || 0}
                  </h5>
                </div>
                <p className="text-sm text-gray-500 fs-5 mt-2">
                  Inclusive of all taxes
                </p>
                <button
                  className={`btn py-11 px-24 rounded-8 flex-center gap-8 fw-medium w-100 my-8 me-16 ${addedToCartMap[product.id]
                    ? "bg-gray-50 text-heading hover-bg-main-600 hover-text-white"
                    : "bg-main-600 text-white"
                    }`}
                  onClick={async () => {
                    if (addedToCartMap[product.id]) {
                      navigate("/cart");
                    } else {
                      await handleAddToCart(product);
                    }
                  }}
                  disabled={loadingAction.id === product.id && loadingAction.type === "add"}
                >
                  {loadingAction.id === product.id && loadingAction.type === "add" ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                      Adding...
                    </>
                  ) : addedToCartMap[product.id] ? (
                    <>
                      <span>Go To Cart</span>
                      <i className="ph ph-shopping-cart" />
                    </>
                  ) : (
                    <>
                      <span>Add To Cart</span>
                      <i className="ph ph-shopping-cart" />
                    </>
                  )}
                </button>
                <button
                  className="btn py-11 px-24 rounded-8 flex-center gap-8 fw-medium w-100 my-8 me-16 btn-outline-main"
                  disabled={loadingAction.id === product.id && loadingAction.type === "buynow"}
                  onClick={(e) => {
                    e.preventDefault();
                    handleBuyNow(product);
                  }}
                >
                  {loadingAction.id === product.id && loadingAction.type === "buynow" ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                      Processing...
                    </>
                  ) : (
                    "Buy Now"
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div >

            </div>
          )}
        </td>
      );
    }
    return cells;
  };
  return (
    <Layout>
      <section className="products-details py-40">
        <div className="container-fluid mb-24">
          <div className="row">
            <div className="col-xl-3"></div>
            {/* Breadcrumb */}
            <div className="col-xl-6">
              <nav className="d-flex flex-wrap align-items-center justify-content-center gap-8 text-sm text-gray-500">
                <Link
                  to="/"
                  className="navigation-bar-prc-det transition-colors text-gray-500 fw-bold text-md"
                >
                  Home
                </Link>
                <span className="text-gray-400">{" > "}</span>
                <Link
                  to="/category-products"
                  className="navigation-bar-prc-det transition-colors text-gray-500 fw-bold text-md"
                >
                  All Categories
                </Link>
                <span className="text-gray-400">{" > "}</span>
                <span className="navigation-bar-prc-det text-gray-400 fw-bold text-md">
                  Product Comparison
                </span>
              </nav>
            </div>
            {/* Compare & Share Icons */}
            <div className="col-xl-3 ">
              <div className="d-flex align-items-center gap-10">
                <label className="d-flex align-items-center  cursor-pointer">
                  <input
                    type="checkbox"
                    className="custom-checkbox me-2 "
                    checked
                  />
                  <span className="navigation-bar-prc-det">Compare</span>
                </label>

                {/* Share Icon */}
                <button
                  className=" btn-btn text-gray-400 m border-none"
                  title="Share"
                  onClick={(e) => {
                    e.preventDefault();
                    if (navigator.share) {
                      navigator.share({
                        // title: product.name,
                        // text: `Check out: ${product.name}`,
                        url: window.location.href,
                      });
                    } else {
                      // Fallback: Copy link to clipboard
                      navigator.clipboard.writeText(window.location.href);
                      alert("Link copied to clipboard!");
                    }
                  }}
                >
                  <i class="fas fa-share"></i>
                  <span className="navigation-bar-prc-det">share</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="container-fluid mb-24 px-32">
          <div className="border border-gray-100 rounded-16 position-relative">
            <h6 className="text-xl ps-32 pe-32 pt-32 pb-3">
              PRODUCT COMPARISION
            </h6>
            <hr />
            <div className="d-flex flex-wrap mb-4 py-32 px-32">
              <div className="row align-items-start">
                <div className=" d-flex justify-content-around">
                  <div className="col-2 ">
                    <h6 className="text-md fw-bold">
                      Compare{" "}
                      {compareList.map((p, idx) => (

                        <span key={p.id} className="ms-5">
                          {p.name.toUpperCase()}
                          {idx < compareList.length - 1 ? " Vs " : ""}
                        </span>
                      ))}
                    </h6>
                    <h6 className="text-md mt-5" style={{ color: "#ff6600" }}>{compareList.length} Items</h6>
                  </div>
                  {compareList.map((p) => {
                    const discountUnitPrice =
                      p.discountMode === "percent"
                        ? parseFloat(p.price) -
                        parseFloat(p.discountValue / 100) * parseFloat(p.price)
                        : parseFloat(p.price) - parseFloat(p.discountValue);

                    const subTotal = Math.round(discountUnitPrice).toFixed(2);
                    return (
                      <div key={p.id} className="col-xl-2 col-lg-3">
                        <div className="compare-card gap-5">
                          <img
                            src={baseURL + "/" + p.mainImage}
                            alt={p.name}
                            className="compare-card-img px-52 py-32 mb-3"
                          />
                          <div className="d-flex flex-wrap justify-content-start ps-16 mt-16">
                            <h6
                              className="text-md fw-bold"
                              style={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: "200px"
                              }}
                            >
                              {p.name.toUpperCase()}
                            </h6>
                            <div className="flex-align gap-8 flex-wrap mb-4">
                              <h6 className="text-xl mb-0 font-semibold text-black">
                                ₹{subTotal}
                              </h6>
                              {p.discountMode && p.discountValue > 0 ? (
                                <span className="text-sm text-main-two-600">
                                  Discount of {p.discountValue}%
                                </span>
                              ) : null}
                            </div>
                            <div className="flex-align gap-2 mt-2">
                              <p className="text-sm text-dark">M.R.P:</p>
                              <h5
                                className="text-xl text-gray-400 mb-0 line-through"
                                style={{ textDecoration: "line-through" }}
                              >
                                ₹{p.price || 0}
                              </h5>
                            </div>
                            <p className="text-sm text-gray-500 fs-5 mt-2">
                              Inclusive of all taxes
                            </p>
                          </div>
                        </div>
                      </div>
                    )
                  })}

                  {/* Placeholder slots */}
                  {Array.from({ length: slotCount }).map((_, idx) => (
                    <div key={idx} className="col-xl-2 col-lg-3">
                      <div className="compare-card gap-5">
                        <img
                          src={""}
                          className="compare-card-img mb-3"
                          style={{ paddingTop: "91px", paddingBottom: "91px", paddingRight: "105px", paddingLeft: "105px" }}
                        />
                        <div className="d-flex flex-wrap justify-content-start ps-16 mt-16">
                          <h6 className="text-md fw-bold">Add a product</h6>
                          <select
                            className="form-select rounded-3"
                            value={slots[idx]?.brandId || ""}
                            onChange={(e) => handleBrandChange(idx, e.target.value)}
                          >
                            <option value="">Select brand</option>
                            {brands.map((b) => (
                              <option key={b.id} value={b.id}>
                                {b.name}
                              </option>
                            ))}
                          </select>
                          <select
                            className="form-select rounded-3 mt-8"
                            value={slots[idx]?.productId || ""}
                            onChange={(e) => handleProductChange(idx, e.target.value)}
                            disabled={!slots[idx]?.brandId}
                          >
                            <option value="">Select product</option>
                            {(brandProducts[slots[idx]?.brandId] || []).map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="comparison-table-wrapper overflow-x-auto">
              <table className="table table-bordered text-center align-middle">
                <tbody>
                  {/* Composition Row */}
                  <tr>
                    <td className="p-32 compare-label-col"><h6 className="text-md fw-bold mb-0">Composition</h6></td>
                    {renderProductCells("composition")}
                  </tr>

                  {/* Grades Available Row */}
                  <tr>
                    <td className="p-32 compare-label-col"><h6 className="text-md fw-bold mb-0">Grades Available</h6></td>
                    {renderProductCells("gradesAvailable")}
                  </tr>

                  {/* Initial Strength Row */}
                  <tr>
                    <td className="p-32 compare-label-col"><h6 className="text-md fw-bold mb-0">Initial Strength</h6></td>
                    {renderProductCells("initialStrength")}
                  </tr>

                  {/* Final Strength Row */}
                  <tr>
                    <td className="p-32 compare-label-col"><h6 className="text-md fw-bold mb-0">Final Strength</h6></td>
                    {renderProductCells("finalStrength")}
                  </tr>

                  {/* Durability Row */}
                  <tr>
                    <td className="p-32 compare-label-col"><h6 className="text-md fw-bold mb-0">Durability</h6></td>
                    {renderProductCells("durability")}
                  </tr>

                  {/* Product Cards Row */}
                  <tr>
                    <td className="p-32 compare-label-col"></td>
                    {renderProductCards()}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>
    </Layout >
  );
};

export default ComparePage;
